/**
 * useFluidTextDrag — Squarespace-Fluid-Engine-style free positioning for
 * inline text (and image) elements inside any builder section.
 *
 * Iteration 143 — direct-element drag: user can grab the element ITSELF
 * (not just the small handle) and drag it freely anywhere inside the
 * section. A 5px threshold separates click-to-edit from drag-to-position.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

// Visual handle dimensions
const HANDLE_SIZE = 22;
const CURSOR_STYLE_ID = 'wb-fluid-cursor-style';

// Iter 146 — Fluid Engine Session 2 (column-based coords).
//   Snap dragging to a fine-grain grid. Persist offsets as
//   { dCol, dRow } deltas relative to the element's natural slot, so
//   positions remain responsive when the section width changes.
//
// Iter 164 — finer grid for near-pixel-level freedom (user request:
// "make the grid line smaller to allow an even more freely drag and drop").
//   Desktop went from 24 cols × 60px rows ➝ 48 cols × 20px rows.
//   That's 2× finer in x and 3× finer in y, so a typical 1200-px section
//   has 25-px × 20-px snap cells instead of 50-px × 60-px bricks.
//
// Migration: previously-saved positions used the 24×60 grid. We scale
// them up on read (×2 cols, ×3 rows) so the user's existing layouts
// stay visually identical without any DB rewrite. The scaling key is
// `__grid_v` stored on the saved offsets dict.
const GRID_COLS = 48;
const GRID_ROW_PX = 20;
const GRID_VERSION = 2;
// Migration factors from grid v1 (24×60) to v2 (48×20).
const _V1_TO_V2 = { col: 2, row: 3 };

function migrateGridOffsets(offsets) {
  if (!offsets || typeof offsets !== 'object') return {};
  if (offsets.__grid_v === GRID_VERSION) return offsets;
  // v1 (legacy) → v2: scale dCol×2, dRow×3 so the visual position is preserved.
  const out = { __grid_v: GRID_VERSION };
  for (const [k, v] of Object.entries(offsets)) {
    if (k === '__grid_v') continue;
    if (v && typeof v === 'object') {
      out[k] = {
        ...v,
        dCol: Math.round((v.dCol || 0) * _V1_TO_V2.col),
        dRow: Math.round((v.dRow || 0) * _V1_TO_V2.row),
      };
    } else {
      out[k] = v;
    }
  }
  return out;
}

function cellWidthFor(section) {
  if (!section) return 0;
  return section.offsetWidth / GRID_COLS;
}

// Per-device storage keys. Desktop is the canonical baseline; tablet and
// mobile carry only OVERRIDES so untouched fields fall back to desktop.
const GRID_KEY_FOR = (device) => (
  device === 'mobile' ? '_fluid_grid_mobile'
  : device === 'tablet' ? '_fluid_grid_tablet'
  : '_fluid_grid'
);

// Applies persisted Fluid-Engine offsets (grid + legacy pixel) to every
// section under `root`. Shared by the editor canvas and Live Preview.
export function applyFluidOffsets(root, blocks, device = 'desktop') {
  if (!root) return;
    const sections = root.querySelectorAll('[data-testid^="live-block-"]');
    sections.forEach(section => {
      const blockId = section.getAttribute('data-block-id');
      const block = (blocks || []).find(b => b.id === blockId);
      const pixelOffsets = block?.content?._fluid_offsets || {};
      // Desktop is baseline; tablet/mobile maps OVERRIDE desktop where present.
      const desktopGrid = migrateGridOffsets(block?.content?._fluid_grid || {});
      const deviceGrid  = device === 'desktop' ? {} : migrateGridOffsets(block?.content?.[GRID_KEY_FOR(device)] || {});
      const gridOffsets = { ...desktopGrid, ...deviceGrid };
      const cellW = cellWidthFor(section);
      let maxBottomShift = 0;

      // Helper — resolve an element by its persistence key
      const elFor = (key) => {
        if (key.startsWith('__image_')) {
          const i = parseInt(key.split('_')[2], 10);
          return section.querySelectorAll('[data-wb-image="1"]')[i];
        }
        if (key.startsWith('__btn_')) {
          const rest = key.slice(6);
          const lastUnderscore = rest.lastIndexOf('_');
          const bField = rest.slice(0, lastUnderscore);
          const idx = parseInt(rest.slice(lastUnderscore + 1), 10);
          return section.querySelectorAll(`[data-wb-button="1"][data-wb-button-field="${bField}"]`)[idx];
        }
        return section.querySelector(`[data-testid="inline-${blockId}-${key}"]`);
      };

      // 1) Column-based offsets (preferred, responsive)
      Object.entries(gridOffsets).forEach(([key, g]) => {
        if (!g) return;
        const el = elFor(key);
        if (!el) return;
        const x = (g.dCol || 0) * cellW;
        const y = (g.dRow || 0) * GRID_ROW_PX;
        // Iter 160 — use setProperty('transform', ..., 'important') so this
        // wins against the global `[data-testid="content-editor"] *:hover {
        // transform: none !important; }` rule that was zeroing the JS
        // transform whenever the cursor was over the element (which is
        // always true mid-drag). User-visible symptom was "elements snap
        // back to origin on release".
        el.style.setProperty('transform', `translate(${x}px, ${y}px)`, 'important');
        el.style.willChange = 'transform';
        if (y > maxBottomShift) maxBottomShift = y;
      });

      // 2) Legacy pixel-only offsets — kept so old layouts stay put
      Object.entries(pixelOffsets).forEach(([key, off]) => {
        if (gridOffsets[key]) return; // grid wins
        if (!off || (typeof off.x !== 'number' && typeof off.y !== 'number')) return;
        const el = elFor(key);
        if (!el) return;
        el.style.setProperty('transform', `translate(${off.x || 0}px, ${off.y || 0}px)`, 'important');
        el.style.willChange = 'transform';
        if ((off.y || 0) > maxBottomShift) maxBottomShift = off.y || 0;
      });

      // Grow section if anything was dragged down
      // Only restore padding we added ourselves, so React-owned section
      // padding (compact/tall/fullbleed heights) is never wiped.
      if (maxBottomShift > 0) {
        if (section.dataset.wbFluidPad !== '1') {
          section.dataset.wbOrigPb = section.style.paddingBottom || '';
          section.dataset.wbFluidPad = '1';
        }
        section.style.setProperty('--wb-fluid-grow', `${maxBottomShift + 80}px`);
        section.style.paddingBottom = `calc(${maxBottomShift + 80}px + 1rem)`;
      } else if (section.dataset.wbFluidPad === '1') {
        section.style.removeProperty('--wb-fluid-grow');
        section.style.paddingBottom = section.dataset.wbOrigPb || '';
        delete section.dataset.wbFluidPad;
        delete section.dataset.wbOrigPb;
      }
    });
}

export function useFluidTextDrag({ canvasRef, blocks, onUpdateBlockContent, enabled = true, fluidGrid = null, device = 'desktop', multiSelect = null }) {
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  const [hoverTarget, setHoverTarget] = useState(null);  // { el, blockId, field, rect }
  const dragState = useRef(null); // { el, blockId, field, startX, startY, baseX, baseY }

  // ─────────────────────────────────────────────────────────────
  // 0) Inject a tiny <style> tag so any hovered fluid target shows
  //    a grab cursor and a subtle dashed outline to advertise drag.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    let el = document.getElementById(CURSOR_STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = CURSOR_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = `
      /* Iter 178p — Final cleanup. The previous box-shadow + border-radius
         hint on [data-wb-fluid-hover] was OVERRIDING the user's button
         border-radius (pill → rect → pill). As the hover attribute toggled
         on/off (which it does as the cursor passes over adjacent text
         spans), the button visibly morphed shape, registering as a flicker.
         Drop the visual hint entirely — keep only the cursor change. */
      [data-wb-fluid-hover="1"] {
        cursor: grab !important;
      }
      [data-wb-fluid-dragging="1"] {
        cursor: grabbing !important;
        opacity: 0.9;
      }
    `;
    return () => {};
  }, [enabled]);

  // ─────────────────────────────────────────────────────────────
  // 1) Apply persisted offsets on every render (cheap; runs on
  //    blocks change). Also bumps host section min-height when a
  //    child has been dragged below its natural bottom.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    applyFluidOffsets(canvasRef?.current || document, blocksRef.current, device);
  }, [blocks, canvasRef, enabled, device]);

  // ─────────────────────────────────────────────────────────────
  // 1b) Re-pixelize on window resize so column-based positions
  //     stay valid when the canvas width changes (desktop ↔ tablet
  //     ↔ mobile, splitter dragged, etc.).
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const reapply = () => {
      const root = canvasRef?.current || document;
      root.querySelectorAll('[data-testid^="live-block-"]').forEach(section => {
        const blockId = section.getAttribute('data-block-id');
        const block = blocksRef.current?.find(b => b.id === blockId);
        const gridOffsets = migrateGridOffsets(block?.content?._fluid_grid || {});
        const cellW = cellWidthFor(section);
        Object.entries(gridOffsets).forEach(([key, g]) => {
          if (!g) return;
          let el = null;
          if (key.startsWith('__image_')) {
            el = section.querySelectorAll('[data-wb-image="1"]')[parseInt(key.split('_')[2], 10)];
          } else if (key.startsWith('__btn_')) {
            const rest = key.slice(6);
            const lastUnderscore = rest.lastIndexOf('_');
            const bField = rest.slice(0, lastUnderscore);
            const idx = parseInt(rest.slice(lastUnderscore + 1), 10);
            el = section.querySelectorAll(`[data-wb-button="1"][data-wb-button-field="${bField}"]`)[idx];
          } else {
            el = section.querySelector(`[data-testid="inline-${blockId}-${key}"]`);
          }
          if (!el) return;
          el.style.setProperty('transform', `translate(${(g.dCol || 0) * cellW}px, ${(g.dRow || 0) * GRID_ROW_PX}px)`, 'important');
        });
      });
    };
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(reapply);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [canvasRef, enabled]);

  // ─────────────────────────────────────────────────────────────
  // 2) Hover tracking — show a floating handle next to whatever
  //    inline-text / image element is under the pointer.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    // Iter 178j — rAF-throttled mousemove. Native mousemove fires 60-120Hz
    // on most displays (240Hz on some). Doing rect math + DOM attribute
    // mutations + React state updates on EVERY event causes visible
    // "shutter" (user's screen-recording bug report). We coalesce all
    // mousemove work into at most one tick per animation frame.
    let rafId = 0;
    let lastEvent = null;
    let lastElRef = null;       // remembers last hovered element across calls
    const processMove = () => {
      rafId = 0;
      const e = lastEvent;
      lastEvent = null;
      if (!e) return;
      if (dragState.current) return; // don't reflow during a drag
      if (typeof document !== 'undefined' && document.body?.dataset?.wbDragging === '1') return;
      const target = e.target;
      if (!target || !target.closest) return;
      if (target.closest('[data-wb-fluid-handle="1"]')) return;
      const inline = target.closest('[data-testid^="inline-"]');
      const image  = target.closest('[data-wb-image="1"]');
      const button = target.closest('[data-wb-button="1"]');
      const el = inline || image || button;
      if (!el) {
        if (lastElRef) {
          lastElRef.removeAttribute('data-wb-fluid-hover');
          lastElRef = null;
        }
        setHoverTarget(prev => prev ? null : prev);
        return;
      }
      // Iter 178j — short-circuit if we already know we're on the same element.
      // Avoids the document.querySelector cost on every frame for stationary
      // mouse over text content (cursor blinking still fires mousemove).
      if (el === lastElRef) {
        // Update rect only if it actually changed (font-hinting can drift it).
        const rect = el.getBoundingClientRect();
        const top    = Math.round(rect.top);
        const left   = Math.round(rect.left);
        const width  = Math.round(rect.width);
        const height = Math.round(rect.height);
        setHoverTarget(prev => {
          if (prev && prev.el === el
            && prev.rect && prev.rect.top === top
            && prev.rect.left === left
            && prev.rect.width === width
            && prev.rect.height === height) return prev;
          // Don't recompute blockId/field — pull from prev.
          return prev
            ? { ...prev, rect: { top, left, width, height } }
            : prev;
        });
        return;
      }
      const blockEl = el.closest('[data-block-id]');
      if (!blockEl) return;
      const blockId = blockEl.getAttribute('data-block-id');
      let field;
      if (inline) {
        const testid = inline.getAttribute('data-testid') || '';
        field = testid.replace(`inline-${blockId}-`, '');
      } else if (image) {
        const idx = image.getAttribute('data-wb-image-index');
        field = `__image_${idx}`;
      } else {
        const idx = button.getAttribute('data-wb-button-index');
        const bField = button.getAttribute('data-wb-button-field') || 'buttons';
        field = `__btn_${bField}_${idx}`;
      }
      const rect = el.getBoundingClientRect();
      const top    = Math.round(rect.top);
      const left   = Math.round(rect.left);
      const width  = Math.round(rect.width);
      const height = Math.round(rect.height);
      // Element actually changed → swap the hover attribute.
      if (lastElRef && lastElRef !== el) lastElRef.removeAttribute('data-wb-fluid-hover');
      el.setAttribute('data-wb-fluid-hover', '1');
      lastElRef = el;
      setHoverTarget({ el, blockId, field, rect: { top, left, width, height } });
    };

    const onMove = (e) => {
      lastEvent = e;
      if (rafId) return; // already scheduled for this frame
      rafId = requestAnimationFrame(processMove);
    };
    const onLeave = () => {
      if (!dragState.current) {
        if (lastElRef) {
          lastElRef.removeAttribute('data-wb-fluid-hover');
          lastElRef = null;
        }
        setHoverTarget(null);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, [enabled]);

  // ─────────────────────────────────────────────────────────────
  // 3) Drag tracking — applies live transform; persists on mouseup.
  // ─────────────────────────────────────────────────────────────
  const beginDragFor = useCallback((e, info) => {
    const { el, blockId, field } = info;
    const block = blocksRef.current?.find(b => b.id === blockId);
    const section = el.closest('[data-testid^="live-block-"]');
    const cellW = cellWidthFor(section);
    // Prefer current device's grid; fall back to desktop, then to legacy pixel.
    const gridKey = GRID_KEY_FOR(device);
    const deviceMap  = migrateGridOffsets(block?.content?.[gridKey] || {});
    const desktopMap = migrateGridOffsets(block?.content?._fluid_grid || {});
    const g = deviceMap[field] || desktopMap[field];
    const p = block?.content?._fluid_offsets?.[field];
    const baseX = g ? (g.dCol || 0) * cellW : (p?.x || 0);
    const baseY = g ? (g.dRow || 0) * GRID_ROW_PX : (p?.y || 0);
    dragState.current = {
      el, blockId, field,
      startX: e.clientX, startY: e.clientY,
      baseX, baseY,
    };
    el.setAttribute('data-wb-fluid-dragging', '1');
    // Show grid + bright mode while a drag is in progress
    fluidGrid?.setDragging?.(true);
    if (section) section.setAttribute('data-wb-grid-active', '1');
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    // Iter 161 — flag the body so floating section controls (the white
    // SectionControlCard menu, hover overlays, BlockToolbar pill, etc.)
    // can hide themselves out of the way while the user is actively
    // dragging an element. CSS rules in DashboardContent.js read this
    // attribute and fade those overlays to opacity:0 + pointer-events:none.
    document.body.dataset.wbDragging = '1';
    // Iter 198g — Ruler HUD: emit drag-start so the ColumnRuler shows itself
    // pinned to this section's top edge.
    const _sr = section?.getBoundingClientRect();
    if (_sr) {
      window.dispatchEvent(new CustomEvent('wb:drag-start', {
        detail: { sectionRect: { top: _sr.top, left: _sr.left, width: _sr.width, height: _sr.height } },
      }));
    }
  }, [fluidGrid, device]);

  const startDrag = useCallback((e) => {
    if (!hoverTarget) return;
    e.preventDefault();
    e.stopPropagation();
    beginDragFor(e, hoverTarget);
  }, [hoverTarget, beginDragFor]);

  // Direct-on-element drag: mousedown on a fluid target, move >5px = drag,
  // release without movement = pass through (click-to-edit / button popover).
  useEffect(() => {
    if (!enabled) return;
    const TRESHOLD = 5;
    const onDown = (e) => {
      if (e.button !== 0) return;
      const target = e.target;
      if (!target.closest) return;
      // Ignore if the user is grabbing the floating handle itself
      if (target.closest('[data-wb-fluid-handle="1"]')) return;
      // Iter 155 — Ignore only if the target is ACTIVELY focused for
      // editing (replacing the previous data-wb-active-edit closest()
      // check, which was stale if focus blurred without the flag being
      // cleared). Use the actual DOM focus state for accuracy.
      const ce = target.closest('[contenteditable="true"]');
      if (ce && document.activeElement === ce) return;
      const inline = target.closest('[data-testid^="inline-"]');
      const image  = target.closest('[data-wb-image="1"]');
      const button = target.closest('[data-wb-button="1"]');
      const el = inline || image || button;
      if (!el) return;
      const blockEl = el.closest('[data-block-id]');
      if (!blockEl) return;
      const blockId = blockEl.getAttribute('data-block-id');
      let field;
      if (inline) {
        const testid = inline.getAttribute('data-testid') || '';
        field = testid.replace(`inline-${blockId}-`, '');
      } else if (image) {
        const idx = image.getAttribute('data-wb-image-index');
        field = `__image_${idx}`;
      } else {
        const idx = button.getAttribute('data-wb-button-index');
        const bField = button.getAttribute('data-wb-button-field') || 'buttons';
        field = `__btn_${bField}_${idx}`;
      }
      const startX = e.clientX, startY = e.clientY;
      let started = false;
      const onWatchMove = (ev) => {
        if (started) return;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.abs(dx) > TRESHOLD || Math.abs(dy) > TRESHOLD) {
          started = true;
          // Cancel anything else that would happen (selection, link, popover)
          ev.preventDefault();
          beginDragFor(e, { el, blockId, field });
          // Iter 155 — suppress the synthetic `click` event that fires
          // after mouseup so the button popover / link navigation does
          // NOT fire as a side-effect of a successful drag. Track A's
          // pain point: clicking a button to drag it would also open the
          // button editor. With this capture-phase one-shot listener,
          // the click is absorbed and the drag stands alone.
          const swallowClick = (cev) => {
            cev.preventDefault();
            cev.stopPropagation();
            cev.stopImmediatePropagation?.();
          };
          document.addEventListener('click', swallowClick, { capture: true, once: true });
          // Safety — drop the listener if no click follows within 250ms
          // (e.g. mouseup outside any element).
          setTimeout(() => document.removeEventListener('click', swallowClick, { capture: true }), 250);
          // Also flag a global timestamp that other handlers (e.g. the
          // section selection mousedown listener) can use to bail.
          window.__wbDragEndedAt = 0; // will be set on mouseup
        }
      };
      const onWatchUp = (upEv) => {
        // Iter 160 — if the gesture turned out to be a CLICK (no drag), and
        // the original target was an InlineText, dispatch a custom event
        // that the InlineText component listens for to enter edit mode.
        // We cannot rely on the React `onClick` reaching the H1 because
        // selectedBlockId-driven re-renders (SectionControlCard) often pop
        // a floating control card directly over the text BETWEEN mousedown
        // and mouseup, which moves the native `click` target off the H1
        // (browsers fire click on the common ancestor of mousedown/mouseup).
        //
        // Iter 161 — only fire when the click happened in the MIDDLE band
        // (25%-75% of the element's height). Clicks on the top/bottom
        // grab-cursor bands are intentionally drag-only and should not
        // enter edit mode.
        if (!started && inline) {
          try {
            const r = inline.getBoundingClientRect();
            const yRel = (upEv.clientY - r.top) / Math.max(r.height, 1);
            const inMiddleBand = yRel > 0.25 && yRel < 0.75;
            if (inMiddleBand) {
              window.dispatchEvent(new CustomEvent('wb:inline-text-click', {
                detail: { blockId, field },
              }));
            }
          } catch { /* noop */ }
        }
        document.removeEventListener('mousemove', onWatchMove, true);
        document.removeEventListener('mouseup', onWatchUp, true);
      };
      document.addEventListener('mousemove', onWatchMove, true);
      document.addEventListener('mouseup', onWatchUp, true);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [enabled, beginDragFor]);

  useEffect(() => {
    if (!enabled) return;
    const onMouseMove = (e) => {
      const d = dragState.current;
      if (!d) return;
      const rawDx = (e.clientX - d.startX) + d.baseX;
      const rawDy = (e.clientY - d.startY) + d.baseY;
      // ── Snap to 48-col × 20-px grid (iter 164) ───────────────────
      const section = d.el.closest('[data-testid^="live-block-"]');
      const cellW = cellWidthFor(section);
      let dCol = cellW > 0 ? Math.round(rawDx / cellW) : 0;
      let dRow = Math.round(rawDy / GRID_ROW_PX);
      let dx = dCol * cellW;
      let dy = dRow * GRID_ROW_PX;

      // ── Iter 165 — Figma-style snap to OTHER elements ─────────────
      // Project where the dragged element will land in viewport coords, then
      // look for nearby sibling edges/centers to snap onto. Whichever sibling
      // axis is closest (within SNAP_PX) wins and we nudge dx/dy by that delta.
      const SNAP_PX = 6;
      const guides = [];
      if (section) {
        const elRect = d.el.getBoundingClientRect();
        // Current element rect already includes the current grid transform.
        // Compute pixel delta from "what we just applied" to figure out the
        // additional shift we'd need to align.
        const appliedDx = d.lastDx ?? 0;
        const appliedDy = d.lastDy ?? 0;
        const projLeft   = elRect.left + (dx - appliedDx);
        const projTop    = elRect.top  + (dy - appliedDy);
        const projRight  = projLeft + elRect.width;
        const projBottom = projTop  + elRect.height;
        const projCx     = projLeft + elRect.width  / 2;
        const projCy     = projTop  + elRect.height / 2;

        // Find every other draggable sibling in this section.
        const siblings = Array.from(section.querySelectorAll(
          '[data-testid^="inline-"],[data-wb-image="1"],[data-wb-button="1"]'
        )).filter(n => n !== d.el && !d.el.contains(n) && !n.contains(d.el));

        // Best snap per axis (smallest delta wins).
        let bestX = null; // { delta, guidePx }
        let bestY = null;
        const consider = (axis, projVal, targetVal) => {
          const delta = targetVal - projVal;
          if (Math.abs(delta) > SNAP_PX) return;
          const slot = axis === 'x' ? 'bestX' : 'bestY';
          const cur = axis === 'x' ? bestX : bestY;
          if (!cur || Math.abs(delta) < Math.abs(cur.delta)) {
            const next = { delta, guidePx: targetVal };
            if (slot === 'bestX') bestX = next; else bestY = next;
          }
        };

        siblings.forEach(sib => {
          const r = sib.getBoundingClientRect();
          const sCx = r.left + r.width / 2;
          const sCy = r.top  + r.height / 2;
          // X axis: left↔left, right↔right, center↔center, left↔right, right↔left
          consider('x', projLeft,  r.left);
          consider('x', projRight, r.right);
          consider('x', projCx,    sCx);
          consider('x', projLeft,  r.right);
          consider('x', projRight, r.left);
          // Y axis
          consider('y', projTop,    r.top);
          consider('y', projBottom, r.bottom);
          consider('y', projCy,     sCy);
          consider('y', projTop,    r.bottom);
          consider('y', projBottom, r.top);
        });

        if (bestX) {
          dx += bestX.delta;
          dCol = cellW > 0 ? Math.round(dx / cellW) : dCol;
          guides.push({ kind: 'v', position: bestX.guidePx });
        }
        if (bestY) {
          dy += bestY.delta;
          dRow = Math.round(dy / GRID_ROW_PX);
          guides.push({ kind: 'h', position: bestY.guidePx });
        }

        const sRect = section.getBoundingClientRect();

        // Iter 198e — 24-column snap grid + horizontal thirds.
        // Squarespace's Fluid Engine snaps to 24 major columns and to the
        // classic composition thirds (33%, 50%, 67%). Only fire when no
        // sibling snap already won that axis so we never pull off a
        // precise element-edge alignment.
        const COL24_SNAP = 8;
        const cellW24 = sRect.width / 24;
        if (!bestX && cellW24 > 0) {
          const relX = projCx - sRect.left;
          const nearestCol = Math.round(relX / cellW24);
          const colX = sRect.left + nearestCol * cellW24;
          const delta = colX - projCx;
          if (Math.abs(delta) < COL24_SNAP && nearestCol >= 0 && nearestCol <= 24) {
            dx += delta;
            dCol = cellW > 0 ? Math.round(dx / cellW) : dCol;
            guides.push({ kind: 'v', position: colX, meta: `col ${nearestCol}/24` });
          }
        }
        if (!bestY) {
          const THIRDS_SNAP = 8;
          const thirds = [
            sRect.top + sRect.height * (1 / 3),
            sRect.top + sRect.height * (2 / 3),
          ];
          const closest = thirds
            .map(y => ({ y, delta: y - projCy }))
            .filter(t => Math.abs(t.delta) < THIRDS_SNAP)
            .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta))[0];
          if (closest) {
            dy += closest.delta;
            dRow = Math.round(dy / GRID_ROW_PX);
            guides.push({ kind: 'h', position: closest.y, meta: 'thirds' });
          }
        }

        // Also keep the section-center / section-edge snap from iter 145,
        // but only if the element-snap didn't already win on that axis
        // (otherwise the section snap could pull us off a precise sibling
        // edge alignment — flagged by code review iter 165).
        const sCx = sRect.left + sRect.width / 2;
        const sCy = sRect.top  + sRect.height / 2;
        const finalLeft  = elRect.left + (dx - appliedDx);
        const finalTop   = elRect.top  + (dy - appliedDy);
        const finalCx    = finalLeft + elRect.width  / 2;
        const finalCy    = finalTop  + elRect.height / 2;
        const SECTION_SNAP = 12;
        if (!bestX && Math.abs(finalCx - sCx) < SECTION_SNAP) {
          dx += (sCx - finalCx);
          dCol = cellW > 0 ? Math.round(dx / cellW) : dCol;
          guides.push({ kind: 'v', position: sCx });
        }
        if (!bestY && Math.abs(finalCy - sCy) < SECTION_SNAP) {
          dy += (sCy - finalCy);
          dRow = Math.round(dy / GRID_ROW_PX);
          guides.push({ kind: 'h', position: sCy });
        }
      }

      d.el.style.setProperty('transform', `translate(${dx}px, ${dy}px)`, 'important');
      d.lastDx = dx;
      d.lastDy = dy;
      d.lastDCol = dCol;
      d.lastDRow = dRow;
      // Group-move: every OTHER selected sibling follows by the same
      // pixel delta during the live drag.
      if (multiSelect?.dragSiblingsBy) {
        const draggedKey = `${d.blockId}|${d.field}`;
        multiSelect.dragSiblingsBy(draggedKey, dCol, dRow, cellW);
      }
      // Live-grow the section as we drag
      if (section && dy > 0) {
        section.style.paddingBottom = `calc(${dy + 80}px + 1rem)`;
      }
      // ── Render guides (element snap + section snap merged) ───────
      if (fluidGrid?.renderSnapGuides) {
        fluidGrid.renderSnapGuides(guides);
      }
      // Iter 198g — Column Ruler HUD update. Broadcast the section rect +
      // final element center so the top-mounted ruler can highlight the
      // active 24-col cell in real time. Dispatched on `window` so the
      // ruler doesn't need to walk React refs to find the drag hook.
      // Iter 198h — also broadcast elCenterY so the left Row Ruler can
      // highlight the vertical position simultaneously.
      const _elFinalRect = d.el.getBoundingClientRect();
      const _elCx = _elFinalRect.left + _elFinalRect.width / 2;
      const _elCy = _elFinalRect.top + _elFinalRect.height / 2;
      // `sRect` is only defined inside the `if (section)` snap-guides block
      // above. Recompute from `section` here so the Column/Row Ruler payload
      // works even on sections that didn't trigger the snap path.
      const _sRectDispatch = section ? section.getBoundingClientRect() : null;
      window.dispatchEvent(new CustomEvent('wb:drag-move', {
        detail: {
          sectionRect: _sRectDispatch ? {
            top: _sRectDispatch.top, left: _sRectDispatch.left,
            width: _sRectDispatch.width, height: _sRectDispatch.height,
          } : null,
          elCenterX: _elCx,
          elCenterY: _elCy,
          elRect: {
            top: _elFinalRect.top, left: _elFinalRect.left,
            width: _elFinalRect.width, height: _elFinalRect.height,
          },
        },
      }));
      // Re-anchor the floating handle to the element's new position
      const rect = d.el.getBoundingClientRect();
      setHoverTarget({ el: d.el, blockId: d.blockId, field: d.field, rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } });
    };
    const onMouseUp = () => {
      const d = dragState.current;
      if (!d) return;
      const dCol = d.lastDCol ?? 0;
      const dRow = d.lastDRow ?? 0;
      const block = blocksRef.current?.find(b => b.id === d.blockId);
      if (block) {
        const gridKey = GRID_KEY_FOR(device);
        const existing = migrateGridOffsets(block.content?.[gridKey] || {});
        const next = { ...existing, [d.field]: { dCol, dRow }, __grid_v: GRID_VERSION };
        // Persist primary update
        const update = { [gridKey]: next };
        // Only clear legacy pixel offset for the desktop-write case
        // (mobile/tablet writes ride on top of desktop baseline).
        if (device === 'desktop') {
          const oldPixel = { ...(block.content?._fluid_offsets || {}) };
          delete oldPixel[d.field];
          update._fluid_offsets = oldPixel;
        }
        // Group-move: persist the same delta for any OTHER selected
        // elements that live in the same block.
        if (multiSelect && multiSelect.selected && multiSelect.selected.size > 1) {
          multiSelect.selected.forEach(key => {
            const [bId, fld] = key.split('|');
            if (bId !== d.blockId) return;     // different block — skip
            if (fld === d.field)   return;     // primary already done
            next[fld] = { dCol, dRow };
          });
        }
        onUpdateBlockContent(d.blockId, { ...(block.content || {}), ...update });
      }
      dragState.current = null;
      d.el.removeAttribute('data-wb-fluid-dragging');
      // Iter 155 — clear the per-section grid overlay marker so the grid
      // truly disappears on mouseup (was previously stuck because only
      // the body flag was cleared).
      const section = d.el.closest('[data-testid^="live-block-"]');
      if (section) section.removeAttribute('data-wb-grid-active');
      fluidGrid?.setDragging?.(false);
      fluidGrid?.renderSnapGuides?.([]);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Iter 161 — restore floating section controls now that drag ended.
      delete document.body.dataset.wbDragging;
      // Iter 198g — Ruler HUD: emit drag-end so the ColumnRuler retracts.
      window.dispatchEvent(new CustomEvent('wb:drag-end'));
      // Iter 155 — stamp a global timestamp so other handlers (section
      // selection, button editor, image toolbar) can defensively bail
      // out of any actions triggered by the synthetic click that follows.
      window.__wbDragEndedAt = Date.now();
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [enabled, onUpdateBlockContent, fluidGrid, device, multiSelect]);

  // Reset on demand (used by the "Snap back" handle button)
  const resetOffset = useCallback(() => {
    if (!hoverTarget) return;
    const { blockId, field, el } = hoverTarget;
    const block = blocksRef.current?.find(b => b.id === blockId);
    if (!block) return;
    const pixelMap = { ...(block.content?._fluid_offsets || {}) };
    const gridMap  = { ...(block.content?._fluid_grid    || {}) };
    delete pixelMap[field];
    delete gridMap[field];
    onUpdateBlockContent(blockId, { ...(block.content || {}), _fluid_offsets: pixelMap, _fluid_grid: gridMap });
    if (el) el.style.transform = '';
  }, [hoverTarget, onUpdateBlockContent]);

  return { hoverTarget, startDrag, resetOffset, HANDLE_SIZE };
}

/**
 * <FluidDragHandle/> — floating handle component to render at the root of
 * the canvas. Pinned to whatever element is hovered. Click-and-drag to
 * move; click "↺" to reset position.
 */
export function FluidDragHandle({ hoverTarget, startDrag, resetOffset }) {
  if (!hoverTarget) return null;
  const { rect } = hoverTarget;
  // Position the handle just outside the top-left of the element
  const top  = rect.top  + window.scrollY - HANDLE_SIZE - 4;
  const left = rect.left + window.scrollX - HANDLE_SIZE - 4;
  return (
    <div
      data-wb-fluid-handle="1"
      data-testid="fluid-drag-handle"
      style={{
        position: 'absolute',
        top, left,
        display: 'flex',
        gap: 2,
        zIndex: 350,
        pointerEvents: 'auto',
      }}
    >
      <button
        onMouseDown={startDrag}
        title="Drag to move (corner = grab)"
        style={{
          width: HANDLE_SIZE, height: HANDLE_SIZE,
          borderRadius: 6,
          background: '#0E0E23',
          border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff',
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          lineHeight: 1,
          boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
        }}
      >
        ✥
      </button>
      <button
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); resetOffset(); }}
        title="Snap back to natural position"
        style={{
          width: HANDLE_SIZE, height: HANDLE_SIZE,
          borderRadius: 6,
          background: '#1A1A2E',
          border: '1px solid rgba(255,255,255,0.14)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          lineHeight: 1,
          boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
        }}
      >
        ↺
      </button>
    </div>
  );
}
